import { ApolloClient } from "@apollo/client";
import { PRODUCT_NAME_QUERY } from "../../apis/taxonomy/queries";
import { TAXONOMY_CLIENT } from "../consts";
import {
  fetchTenantPreferences,
  TenantPreferences,
} from "../../apis/argo/tenantPreferences";
import { SAMConfig, SOMConfig, Workspace } from "../../types/workspace";

export type ResolvedConfiguration = Pick<
  Workspace["configuration"],
  "sam" | "som" | "competitorsProductIds"
>;

export interface ResolverUnresolved {
  industries: string[];
  excludedIndustries: string[];
  countries: string[];
  excludedCountries: string[];
  products: string[];
  excludedProducts: string[];
  competitors: string[];
  companySizes: string[];
}

export interface ResolverResolvedNames {
  industries: string[];
  excludedIndustries: string[];
  countries: string[];
  excludedCountries: string[];
  products: string[];
  excludedProducts: string[];
  competitors: string[];
  companySizes: string[];
}

export interface ResolverResult {
  configuration: ResolvedConfiguration;
  unresolved: ResolverUnresolved;
  resolved: ResolverResolvedNames;
}

interface TaxonomyIndustry {
  id: number;
  name: string;
}

interface TaxonomyCountry {
  id: string;
  alpha2: string;
  name: string;
}

export interface ResolverTaxonomy {
  hgIndustries: TaxonomyIndustry[];
  countries: TaxonomyCountry[];
}

const EMPTY_RANGE = { min: undefined, max: undefined };

const COMPANY_SIZE_BUCKETS: Record<string, { min: number; max: number }> = {
  "1-10": { min: 1, max: 10 },
  "10-100": { min: 10, max: 100 },
  "100-1000": { min: 100, max: 1000 },
  "1000-10000": { min: 1000, max: 10000 },
  "10000+": { min: 10000, max: Number.MAX_SAFE_INTEGER },
};

function buildNameIndex<T extends { name: string }>(
  items: T[]
): Map<string, T> {
  const index = new Map<string, T>();
  for (const item of items) {
    index.set(item.name.trim().toLowerCase(), item);
  }
  return index;
}

function resolveIndustries(
  names: string[],
  industriesByName: Map<string, TaxonomyIndustry>
): { ids: number[]; resolved: string[]; unresolved: string[] } {
  const ids: number[] = [];
  const resolved: string[] = [];
  const unresolved: string[] = [];
  for (const name of names) {
    const match = industriesByName.get(name.trim().toLowerCase());
    if (match) {
      ids.push(match.id);
      resolved.push(match.name);
    } else {
      unresolved.push(name);
    }
  }
  return { ids, resolved, unresolved };
}

function resolveCountries(
  alpha2Codes: string[],
  countriesByAlpha2: Map<string, TaxonomyCountry>
): { ids: string[]; resolved: string[]; unresolved: string[] } {
  const ids: string[] = [];
  const resolved: string[] = [];
  const unresolved: string[] = [];
  for (const code of alpha2Codes) {
    const match = countriesByAlpha2.get(code.trim().toUpperCase());
    if (match) {
      // `countryIds` in Workspace.configuration is keyed by alpha-2, not the
      // taxonomy's numeric/string `id` — see geographyTransformer.ts which maps
      // `countryIds` straight to `countryAlpha2s` for downstream APIs.
      ids.push(match.alpha2);
      resolved.push(match.name);
    } else {
      unresolved.push(code);
    }
  }
  return { ids, resolved, unresolved };
}

async function resolveProductsByName(
  apolloClient: ApolloClient<unknown>,
  names: string[]
): Promise<{ ids: number[]; resolved: string[]; unresolved: string[] }> {
  if (names.length === 0) return { ids: [], resolved: [], unresolved: [] };

  const results = await Promise.all(
    names.map(async (name) => {
      try {
        const { data } = await apolloClient.query<{
          product: { data: { id: number; name: string }[] };
        }>({
          query: PRODUCT_NAME_QUERY,
          variables: { name },
          context: { clientName: TAXONOMY_CLIENT },
          fetchPolicy: "no-cache",
        });
        const matches = data?.product?.data ?? [];
        return { name, match: matches[0] };
      } catch {
        return { name, match: undefined };
      }
    })
  );

  const ids: number[] = [];
  const resolved: string[] = [];
  const unresolved: string[] = [];
  for (const { name, match } of results) {
    if (match) {
      ids.push(match.id);
      resolved.push(match.name);
    } else {
      unresolved.push(name);
    }
  }
  return { ids, resolved, unresolved };
}

function formatEmployeesRange(
  min: number | undefined,
  max: number | undefined
): string {
  if (min === undefined && max === undefined) return "";
  const fmt = (n: number) => n.toLocaleString("en-US");
  if (max === Number.MAX_SAFE_INTEGER && min !== undefined) {
    return `${fmt(min)}+`;
  }
  if (min !== undefined && max !== undefined)
    return `${fmt(min)} – ${fmt(max)}`;
  if (min !== undefined) return `${fmt(min)}+`;
  return `up to ${fmt(max as number)}`;
}

function resolveEmployeeRange(buckets: string[]): {
  range: SOMConfig["employeeRange"];
  resolved: string[];
  unresolved: string[];
} {
  if (buckets.length === 0) {
    return { range: EMPTY_RANGE, resolved: [], unresolved: [] };
  }

  let hasResolved = false;
  const unresolved: string[] = [];
  let min: number | undefined;
  let max: number | undefined;

  for (const bucket of buckets) {
    const range = COMPANY_SIZE_BUCKETS[bucket];
    if (!range) {
      unresolved.push(bucket);
      continue;
    }
    hasResolved = true;
    min = min === undefined ? range.min : Math.min(min, range.min);
    max = max === undefined ? range.max : Math.max(max, range.max);
  }

  const resolved = hasResolved ? [formatEmployeesRange(min, max)] : [];
  return { range: { min, max }, resolved, unresolved };
}

export async function resolveTenantPreferences(
  apolloClient: ApolloClient<unknown>,
  taxonomy: ResolverTaxonomy,
  preferences?: TenantPreferences | null
): Promise<ResolverResult> {
  const prefs = preferences ?? (await fetchTenantPreferences());

  const empty: ResolverResult = {
    configuration: {
      sam: {
        industry: {
          ids: [],
          sicIds: [],
          naicsIds: [],
          excludedIds: [],
          excludedSicIds: [],
          excludedNaicsIds: [],
        },
        geography: {
          regionsIds: [],
          countryIds: [],
          stateIds: [],
          excludedCountryIds: [],
        },
      },
      som: {
        employeeRange: EMPTY_RANGE,
        revenueRange: EMPTY_RANGE,
        productIds: [],
        productsInclusionMethod: "ANY_PRESENT",
        excludedProductIds: [],
        excludedProductsInclusionMethod: "NONE_PRESENT",
      },
      competitorsProductIds: [],
    },
    unresolved: {
      industries: [],
      excludedIndustries: [],
      countries: [],
      excludedCountries: [],
      products: [],
      excludedProducts: [],
      competitors: [],
      companySizes: [],
    },
    resolved: {
      industries: [],
      excludedIndustries: [],
      countries: [],
      excludedCountries: [],
      products: [],
      excludedProducts: [],
      competitors: [],
      companySizes: [],
    },
  };

  if (!prefs) return empty;

  const industriesByName = buildNameIndex(taxonomy.hgIndustries);
  const countriesByAlpha2 = new Map<string, TaxonomyCountry>();
  for (const c of taxonomy.countries) {
    countriesByAlpha2.set(c.alpha2.toUpperCase(), c);
  }

  const industries = resolveIndustries(
    prefs.icp?.industries ?? [],
    industriesByName
  );
  const excludedIndustries = resolveIndustries(
    prefs.anti_icp?.industries ?? [],
    industriesByName
  );
  const countries = resolveCountries(
    prefs.icp?.countries ?? [],
    countriesByAlpha2
  );
  const excludedCountries = resolveCountries(
    prefs.anti_icp?.countries ?? [],
    countriesByAlpha2
  );

  const [products, excludedProducts, competitors] = await Promise.all([
    resolveProductsByName(apolloClient, prefs.products ?? []),
    resolveProductsByName(apolloClient, prefs.excluded_products ?? []),
    resolveProductsByName(apolloClient, prefs.competitors ?? []),
  ]);

  const employees = resolveEmployeeRange(prefs.icp?.company_sizes ?? []);

  const sam: SAMConfig = {
    industry: {
      ids: industries.ids,
      sicIds: [],
      naicsIds: [],
      excludedIds: excludedIndustries.ids,
      excludedSicIds: [],
      excludedNaicsIds: [],
    },
    geography: {
      regionsIds: [],
      countryIds: countries.ids,
      stateIds: [],
      excludedCountryIds: excludedCountries.ids,
    },
  };

  const som: SOMConfig = {
    employeeRange: employees.range,
    revenueRange: EMPTY_RANGE,
    productIds: products.ids,
    productsInclusionMethod: "ANY_PRESENT",
    excludedProductIds: excludedProducts.ids,
    excludedProductsInclusionMethod: "NONE_PRESENT",
  };

  return {
    configuration: { sam, som, competitorsProductIds: competitors.ids },
    unresolved: {
      industries: industries.unresolved,
      excludedIndustries: excludedIndustries.unresolved,
      countries: countries.unresolved,
      excludedCountries: excludedCountries.unresolved,
      products: products.unresolved,
      excludedProducts: excludedProducts.unresolved,
      competitors: competitors.unresolved,
      companySizes: employees.unresolved,
    },
    resolved: {
      industries: industries.resolved,
      excludedIndustries: excludedIndustries.resolved,
      countries: countries.resolved,
      excludedCountries: excludedCountries.resolved,
      products: products.resolved,
      excludedProducts: excludedProducts.resolved,
      competitors: competitors.resolved,
      companySizes: employees.resolved,
    },
  };
}

export function hasUnresolved(unresolved: ResolverUnresolved): boolean {
  return Object.values(unresolved).some((arr) => arr.length > 0);
}

export function summarizeUnresolved(unresolved: ResolverUnresolved): string {
  const parts: string[] = [];
  for (const { key, label } of UNRESOLVED_LABELS) {
    if (unresolved[key].length > 0) {
      parts.push(`${unresolved[key].length} ${label}`);
    }
  }
  return parts.join(", ");
}

export interface UnresolvedSection {
  label: string;
  items: string[];
}

const UNRESOLVED_LABELS: Array<{
  key: keyof ResolverUnresolved;
  label: string;
}> = [
  { key: "industries", label: "Industries" },
  { key: "excludedIndustries", label: "Excluded industries" },
  { key: "countries", label: "Countries" },
  { key: "excludedCountries", label: "Excluded countries" },
  { key: "products", label: "Products" },
  { key: "excludedProducts", label: "Excluded products" },
  { key: "competitors", label: "Competitors" },
  { key: "companySizes", label: "Company sizes" },
];

export function unresolvedSections(
  unresolved: ResolverUnresolved
): UnresolvedSection[] {
  return UNRESOLVED_LABELS.filter(({ key }) => unresolved[key].length > 0).map(
    ({ key, label }) => ({ label, items: unresolved[key] })
  );
}

export interface ResolvedCounts {
  industries: number;
  excludedIndustries: number;
  countries: number;
  excludedCountries: number;
  products: number;
  excludedProducts: number;
  competitors: number;
  companySizes: number;
}

const SECTION_LABEL_SINGULAR: Record<string, string> = {
  Industries: "Industry",
  "Excluded Industries": "Excluded Industry",
  "Excluded industries": "Excluded industry",
  Countries: "Country",
  "Excluded Countries": "Excluded Country",
  "Excluded countries": "Excluded country",
  Products: "Product",
  "Excluded Products": "Excluded Product",
  "Excluded products": "Excluded product",
  Competitors: "Competitor",
  "Employees Range": "Employees Range",
};

export function pluralizeSectionLabel(label: string, count: number): string {
  if (count === 1) return SECTION_LABEL_SINGULAR[label] ?? label;
  return label;
}

export function countResolved(result: ResolverResult): ResolvedCounts {
  const { configuration: c } = result;
  return {
    industries: c.sam.industry.ids.length,
    excludedIndustries: c.sam.industry.excludedIds.length,
    countries: c.sam.geography.countryIds.length,
    excludedCountries: c.sam.geography.excludedCountryIds.length,
    products: c.som.productIds.length,
    excludedProducts: c.som.excludedProductIds.length,
    competitors: c.competitorsProductIds.length,
    companySizes: c.som.employeeRange.min !== undefined ? 1 : 0,
  };
}

const RESOLVED_LABELS: Array<{ key: keyof ResolvedCounts; label: string }> = [
  { key: "industries", label: "industries" },
  { key: "excludedIndustries", label: "excluded industries" },
  { key: "countries", label: "countries" },
  { key: "excludedCountries", label: "excluded countries" },
  { key: "products", label: "products" },
  { key: "excludedProducts", label: "excluded products" },
  { key: "competitors", label: "competitors" },
  { key: "companySizes", label: "company size range" },
];

export function summarizeResolved(counts: ResolvedCounts): string {
  const parts: string[] = [];
  for (const { key, label } of RESOLVED_LABELS) {
    if (counts[key] > 0) parts.push(`${counts[key]} ${label}`);
  }
  return parts.join(", ");
}

export type SectionGroup = "sam" | "som" | "competitors";

const RESOLVED_GROUPS: Record<
  SectionGroup,
  Array<{ key: keyof ResolvedCounts; label: string }>
> = {
  sam: [
    { key: "industries", label: "Industries" },
    { key: "excludedIndustries", label: "Excluded Industries" },
    { key: "countries", label: "Countries" },
    { key: "excludedCountries", label: "Excluded Countries" },
  ],
  som: [
    { key: "companySizes", label: "Employees Range" },
    { key: "products", label: "Products" },
    { key: "excludedProducts", label: "Excluded Products" },
  ],
  competitors: [{ key: "competitors", label: "Competitors" }],
};

const UNRESOLVED_GROUPS: Record<
  SectionGroup,
  Array<{ key: keyof ResolverUnresolved; label: string }>
> = {
  sam: [
    { key: "industries", label: "Industries" },
    { key: "excludedIndustries", label: "Excluded industries" },
    { key: "countries", label: "Countries" },
    { key: "excludedCountries", label: "Excluded countries" },
  ],
  som: [
    { key: "companySizes", label: "Employees Range" },
    { key: "products", label: "Products" },
    { key: "excludedProducts", label: "Excluded products" },
  ],
  competitors: [{ key: "competitors", label: "Competitors" }],
};

export interface GroupedResolved {
  group: SectionGroup;
  label: string;
  summary: string;
}

export function groupedResolved(
  counts: ResolvedCounts,
  resolved?: ResolverResolvedNames
): GroupedResolved[] {
  const labelByGroup: Record<SectionGroup, string> = {
    sam: "SAM",
    som: "SOM",
    competitors: "Competitors",
  };
  const out: GroupedResolved[] = [];
  for (const group of Object.keys(RESOLVED_GROUPS) as SectionGroup[]) {
    const parts: string[] = [];
    const groupLabelLower = labelByGroup[group].toLowerCase();
    for (const { key, label } of RESOLVED_GROUPS[group]) {
      if (counts[key] === 0) continue;
      if (key === "companySizes") {
        const rangeValue = resolved?.companySizes[0];
        parts.push(rangeValue ? `Employees: ${rangeValue}` : label);
        continue;
      }
      const sublabel = label.toLowerCase() === groupLabelLower ? "" : label;
      const pluralized = sublabel
        ? pluralizeSectionLabel(sublabel, counts[key])
        : "";
      parts.push(
        pluralized ? `${counts[key]} ${pluralized}` : String(counts[key])
      );
    }
    if (parts.length > 0) {
      out.push({
        group,
        label: labelByGroup[group],
        summary: parts.join(", "),
      });
    }
  }
  return out;
}

export interface GroupedUnresolved {
  group: SectionGroup;
  label: string;
  sections: UnresolvedSection[];
  total: number;
}

export interface GroupedResolvedNames {
  group: SectionGroup;
  label: string;
  sections: UnresolvedSection[];
}

export function groupedResolvedNames(
  resolved: ResolverResolvedNames
): GroupedResolvedNames[] {
  const labelByGroup: Record<SectionGroup, string> = {
    sam: "SAM",
    som: "SOM",
    competitors: "Competitors",
  };
  const out: GroupedResolvedNames[] = [];
  for (const group of Object.keys(UNRESOLVED_GROUPS) as SectionGroup[]) {
    const sections: UnresolvedSection[] = [];
    for (const { key, label } of UNRESOLVED_GROUPS[group]) {
      if (resolved[key].length > 0) {
        sections.push({ label, items: resolved[key] });
      }
    }
    if (sections.length > 0) {
      out.push({ group, label: labelByGroup[group], sections });
    }
  }
  return out;
}

export function groupedUnresolved(
  unresolved: ResolverUnresolved
): GroupedUnresolved[] {
  const labelByGroup: Record<SectionGroup, string> = {
    sam: "SAM",
    som: "SOM",
    competitors: "Competitors",
  };
  const out: GroupedUnresolved[] = [];
  for (const group of Object.keys(UNRESOLVED_GROUPS) as SectionGroup[]) {
    const sections: UnresolvedSection[] = [];
    let total = 0;
    for (const { key, label } of UNRESOLVED_GROUPS[group]) {
      if (unresolved[key].length > 0) {
        sections.push({ label, items: unresolved[key] });
        total += unresolved[key].length;
      }
    }
    if (sections.length > 0) {
      out.push({ group, label: labelByGroup[group], sections, total });
    }
  }
  return out;
}

export function isResultEmpty(result: ResolverResult): boolean {
  const counts = countResolved(result);
  const hasResolved = Object.values(counts).some((n) => n > 0);
  return !hasResolved && !hasUnresolved(result.unresolved);
}
